'use client';
import { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Button } from '@ui/button';
import { useRouter } from 'next/router';
import { Icon } from '@ui/icons';
import { useReporter } from '@web/reporter';
import { useToast } from '@web/Toast';
import { Dig } from '@web/analysis/dig';
import { ErrorText } from '@web/atoms';
import { Modal } from '@web/components';
import { ScreeningStepResultHeader } from '../ScreeningStepResultHeader';
import { ScreeningStepHeader } from '../ScreeningStepHeader';
import {
  HealthScreeningContextActionName,
  useHealthScreening,
} from '../HealthScreeningContainer/context';
import { PHOTO_UPLOAD_ERROR_MSG } from './constants';
import {
  StyledButtonContainer,
  StyledCaptureInput,
  StyledIconContainer,
  StyledList,
  StyledListItem,
  StyledListItemText,
  StyledListItems,
  StyledReviewContainer,
  StyledReviewImageContainer,
  StyledReviewListTitle,
  StyledReviewTitle,
  StyledUploadImageContainer,
  ErrorTextWrapper,
} from './styles';

const checkForBlankImage = potentialImage => {
  const image = potentialImage || '';
  return image.match(/^A+=*$/);
};

const toBase64 = file =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
  });

export const UploadImageContainer = ({
  buttonText,
  reviewModal,
  stepTitle,
  stepDescription,
  stepResult,
  headerImage,
}) => {
  const reporter = useReporter();
  const captureRef = useRef(null);
  const { dispatch: screeningDispatch } = useHealthScreening();
  const { showToast } = useToast();
  const router = useRouter();
  const [uploading, setUploading] = useState(false);
  const [selectedImage, setSelectedImage] = useState(undefined);
  const [error, setError] = useState(false);

  const closeReviewModal = () => {
    reporter.tag({
      type: 'User closes review modal to select another image',
      data: {
        flow: 'web_weight_acquisition',
      },
    });
    setSelectedImage(undefined);
  };

  const bodyConditionStatus = router?.query?.body_condition;

  const selectPhoto = async e => {
    reporter.tag({
      type: 'Clicks Add a Pic',
      data: {
        flow: 'weight_web_acquisition',
      },
    });
    await captureRef?.current.click();
  };

  const onImageSelected = async e => {
    const imageFile = e.target.files[0];

    if (imageFile) {
      try {
        const encodedImage = (await toBase64(imageFile)) || '';
        const isBlank = checkForBlankImage(encodedImage);

        if (isBlank) {
          reporter.tag({
            type: `Error: All-Zeroes Photo Detected`,
            data: {
              encoded_photo_size: encodedImage.length,
              flow: 'weight_web_acquisition',
            },
          });

          showToast({
            status: 'info',
            headline: 'Info',
            message: 'Please try taking the photo again',
          });
          return;
        }
        setSelectedImage(encodedImage);
        reporter.tag({
          type: 'Image review modal opens',
          data: {
            flow: 'web_weight_acquisition',
          },
        });
      } catch (e) {
        if (e === 'cancel') {
          setSelectedImage(undefined);
          return;
        } else {
          setSelectedImage(undefined);
          showToast({
            status: 'error',
            headline: 'Error',
            message: 'There was a problem. Try again.',
          });
          setError(PHOTO_UPLOAD_ERROR_MSG);
          throw e;
        }
      }
    }
  };

  const onCloseModal = () => {
    setSelectedImage(undefined);
  };

  const upload = async () => {
    if (selectedImage) {
      try {
        setUploading(true);

        const imageId = await Dig.uploadPhoto(selectedImage);

        showToast({
          status: 'success',
          headline: 'Success',
          message: 'Picture uploaded!',
          icon: '📷',
        });

        reporter.tag({
          type: 'Uploads Photo',
          data: {
            flow: 'weight_web_acquisition',
          },
        });

        onCloseModal();
        screeningDispatch({
          type: HealthScreeningContextActionName.setImageId,
          payload: { imageId },
        });
        router.push({ pathname: `/health/weight/email` });
      } catch (e) {
        showToast({
          status: 'error',
          headline: 'Error',
          message: 'There was a problem. Try again.',
        });
        setUploading(false);
        throw e;
      }
    }
  };
  return (
    <StyledUploadImageContainer>
      {bodyConditionStatus ? (
        <ScreeningStepResultHeader {...stepResult} />
      ) : (
        <ScreeningStepHeader
          title={stepTitle}
          description={stepDescription}
          image={headerImage}
        />
      )}
      <StyledButtonContainer>
        <Button
          variant="yam"
          onClick={selectPhoto}
          fullWidth
          isLoading={uploading}
        >
          {bodyConditionStatus ? stepResult.ctaText : buttonText}
        </Button>
      </StyledButtonContainer>
      {error && (
        <ErrorTextWrapper>
          <ErrorText>{error}</ErrorText>
        </ErrorTextWrapper>
      )}
      <StyledCaptureInput
        type="file"
        accept="image/*"
        ref={captureRef}
        onChange={onImageSelected}
        // clear value to force onChange and allow
        // uploading of the same photo
        onClick={({ target }) => (target.value = '')}
      />
      <Modal
        shown={selectedImage}
        mobileFullWidthOnly
        mobilePopUp={true}
        onCloseModal={closeReviewModal}
      >
        <StyledReviewContainer>
          <StyledReviewImageContainer>
            <img src={selectedImage} alt={'User Selected'} />
          </StyledReviewImageContainer>
          <StyledReviewTitle>{reviewModal.title}</StyledReviewTitle>
          <StyledList>
            <StyledReviewListTitle>
              {reviewModal.listTitle}
            </StyledReviewListTitle>
            <StyledListItems>
              {reviewModal.list?.map(item => (
                <StyledListItem key={item.attribute}>
                  <StyledIconContainer>
                    <Icon
                      iconName="check"
                      color="secondary.black"
                      size={'1rem'}
                    />
                  </StyledIconContainer>
                  <StyledListItemText>{item.attribute}</StyledListItemText>
                </StyledListItem>
              ))}
            </StyledListItems>
          </StyledList>
          <StyledButtonContainer>
            <Button
              variant="yam"
              onClick={upload}
              fullWidth
              isLoading={uploading}
              disabled={uploading}
            >
              {reviewModal.primaryButtonText}
            </Button>
          </StyledButtonContainer>
          <StyledButtonContainer>
            <Button
              variant="lightest"
              onClick={closeReviewModal}
              fullWidth
              disabled={uploading}
            >
              {reviewModal.secondaryButtonText}
            </Button>
          </StyledButtonContainer>
        </StyledReviewContainer>
      </Modal>
    </StyledUploadImageContainer>
  );
};

UploadImageContainer.displayName = 'UploadImageContainer';
UploadImageContainer.propTypes = {
  buttonText: PropTypes.string,
  stepTitle: PropTypes.string,
  stepDescription: PropTypes.string,
  headerImage: PropTypes.object,
  reviewModal: PropTypes.shape({
    list: PropTypes.array,
    listTitle: PropTypes.string,
  }),
  stepResult: PropTypes.shape({
    title: PropTypes.string,
    description: PropTypes.string,
    headerImage: PropTypes.object,
  }),
};
